import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Link,
  Typography,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Project } from "../../types/project";
import { useNavigate } from "react-router-dom";
import { useGetProjectsQuery } from "../../services/project.service";
import dayjs from "dayjs";
import ProjectDrawer from "./ProjectDrawer";
import ChipProjectStatus from "../../components/ChipProjectStatus";
import { formatWithTimezone } from "../../utils/dateHelper";
import CompanyDrawer from "../company/CompanyDrawer";
import { StyledTextField, Button, GlassDataGrid, CardBase, OrangeLinearProgress } from "../../components/global";
import { Download as DownloadIcon } from "@mui/icons-material";

const ProjectsPage = () => {
  useTitle("Jobs");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState(null);

  const [openCompany, setOpenCompany] = React.useState(false);
  const [currentCompany, setCurrentCompany] = React.useState(null);
  const onViewCompany = (item: any) => {
    setCurrentCompany(item);
    setOpenCompany(true);
  };

  const { data, isLoading, isFetching } = useGetProjectsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : "start_date",
    sortMode: sortModel.length > 0 ? sortModel[0].sort : "desc",
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const handleEditRow = (row: GridRowModel<Project>) => {
    //
  };

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "job_title",
        headerName: "Title",
        minWidth: isMobile ? 200 : 400,
        flex: 1,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => onView(params.row)}
            sx={{
              color: "#F4A259",
              "&:hover": {
                color: "#E8944A",
                textDecoration: "underline",
              },
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            }}
          >
            #{params.row.id} - {params.row.title}
          </Link>
        ),
        disableExport: true,
      },
      {
        field: "id",
        headerName: "ID",
        hide: true,
      },
      {
        field: "title",
        headerName: "Title",
        hide: true,
      },
      {
        field: "company.name",
        headerName: "Company",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          `${params.row?.company?.name}`,
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => onViewCompany(params.row.company)}
            sx={{
              color: "#F4A259",
              "&:hover": {
                color: "#E8944A",
                textDecoration: "underline",
              },
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            }}
          >
            {params.row?.company?.name || "N/A"}
          </Link>
        ),
      },
      {
        field: "customer_id",
        headerName: "Client / Location",
        headerAlign: "center",
        minWidth: isMobile ? 120 : 250,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.customer ? (
            <Typography variant="body2" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
              {params.row.customer.first_name} {params.row.customer.last_name}
            </Typography>
          ) : !!params.row.location ? (
            <Typography variant="body2" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>{params.row.location.name}</Typography>
          ) : (
            <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>?</Typography>
          ),
        disableExport: true,
      },
      {
        field: "customer",
        headerName: "Client/Location",
        hide: true,
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          !!params.row.customer
            ? `${params.row.customer.first_name} ${params.row.customer.last_name}`
            : !!params.row.location
              ? params.row.location.name
              : `?`,
      },
      {
        field: "state_city",
        headerName: "City, State",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 200,
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          !!params.row.customer
            ? `${params.row.customer.city}, ${params.row.customer.state} ${params.row.customer.zip}`
            : !!params.row.location
              ? `${params.row.location.city}, ${params.row.location.state} ${params.row.location.zip}`
              : `?`,
      },
      {
        field: "is_private",
        headerName: "Is Private",
        headerAlign: "center",
        minWidth: isMobile ? 60 : 50,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.is_private ? (
            <Chip label="Yes" color="default" size="small" sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }} />
          ) : (
            <Typography variant="body2" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>No</Typography>
          ),
      },
      {
        field: "created_at",
        headerName: "Created At",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 200,
        sortable: true,
        filterable: true,
        align: "left",
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          `${formatWithTimezone(
            params.row.created_at,
            params.row.timezone,
            "MMM D, YYYY - h:mmA z"
          )}`,
      },
      {
        field: "start_at",
        headerName: "Start At",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 200,
        sortable: true,
        filterable: true,
        align: "left",
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          `${formatWithTimezone(
            params.row.start_date,
            params.row.timezone,
            "MMM D, YYYY - h:mmA z"
          )}`,
      },
      {
        field: "status",
        headerName: "Status",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <ChipProjectStatus status={params.row.status} />
        ),
      },
      {
        field: "reason",
        headerName: "Reason",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          params.row.canceled_reason,
      },
      {
        field: "type",
        headerName: "Job Type",
        hide: true,
      },
      {
        field: "percentage_uplift",
        headerName: "Percentage Uplift",
        hide: true,
      },
      {
        field: "tool_required",
        headerName: "Tool Required",
        hide: true,
      },
      {
        field: "is_photo_required",
        headerName: "Photo Required",
        hide: true,
      },
    ],
    [isMobile]
  );


  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  const onView = (item: any) => {
    setCurrentItem(item);
    setOpen(true);
  };

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns (not hidden)
    const visibleColumns = columns.filter((col) => !col.hide && col.field);

    // Create CSV header
    const headers = visibleColumns.map((col) => col.headerName || col.field).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns.map((col) => {
        let value: string = "";

        // Use valueGetter if available
        if (col.valueGetter) {
          const params = { row } as GridValueGetterParams<undefined, Project>;
          const getterValue = col.valueGetter(params);
          value = getterValue != null && getterValue !== undefined
            ? String(getterValue)
            : "";
        } else {
          // Try to get value from row
          const fieldParts = col.field?.split(".") || [];
          const rowValue = fieldParts.reduce((obj: any, part: string) => obj?.[part], row);
          value = rowValue != null && rowValue !== undefined
            ? String(rowValue)
            : "";
        }

        // Escape commas and quotes
        value = value.replace(/"/g, '""');
        if (value.includes(",") || value.includes('"') || value.includes("\n")) {
          value = `"${value}"`;
        }
        return value;
      }).join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Jobs_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  return (
    <>
      <CardBase
        title="Jobs"
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
            {!!query && (
              <Button
                variant="text"
                onClick={() => setQuery("")}
                fullWidth={isMobile}
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!rows || rows.length === 0}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
                "&:disabled": {
                  borderColor: "rgba(244, 162, 89, 0.5)",
                  color: "rgba(244, 162, 89, 0.5)",
                },
              }}
            >
              {isMobile ? "Export" : "Export CSV"}
            </Button>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          height={isMobile ? "calc(85vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(1),
                paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(1),
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(1),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(1),
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ProjectDrawer data={currentItem!} open={open} setOpen={setOpen} />
      <CompanyDrawer
        data={currentCompany!}
        open={openCompany}
        setOpen={setOpenCompany}
        forceRefresh={true}
      />
    </>
  );
};
export default ProjectsPage;
